import React from "react";
import { get, reduce } from "lodash";
import styled from "styled-components";
import Menu from "./menu";
import Toggle from "./toggle";

const VIEWPORT_MARGIN = 16;

const diffRect = (rectA, rectB): Record<string, any> =>
  reduce(
    rectB,
    (acc, val, key) => {
      let diff;
      if (key === "right" || key === "bottom") {
        diff = rectB[key] - rectA[key];
      } else {
        diff = rectA[key] - rectB[key];
      }
      if (diff < 0) {
        if (acc) {
          acc[key] = diff;
        } else {
          acc = { [key]: diff };
        }
      }
      return acc;
    },
    {}
  );

const Container = styled.div`
  max-width: 100%;
  position: relative;
`;

const isOutsideViewport = (clientRect) => {
  const viewPortRect = {
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    left: 0,
  };

  return diffRect(clientRect, viewPortRect);
};

interface IProps {
  open?: boolean;
  pullRight?: boolean;
  onToggle?: (open: boolean) => any;
  className?: string;
  dropUp?: boolean;
  children: React.ReactNode;
}

const isDropdownElement = (
  obj: Record<string, any>,
  displayName: string
): boolean => {
  return (
    React.isValidElement(obj) &&
    get(obj, "type.displayName", "").indexOf(displayName) !== -1
  );
};

const KomonDropdown = ({
  className,
  children,
  onToggle,
  pullRight = false,
  open = false,
  dropUp = false,
}: IProps) => {
  const dropdownReference = React.useRef<HTMLDivElement>();
  const menuReference = React.useRef<HTMLDivElement>();
  const [innerOpen, setOpen] = React.useState(open);
  const [innerPullRight, setPullRight] = React.useState(pullRight);
  const [innerDropUp, setDropUp] = React.useState(dropUp);
  const [maxHeight, setMaxHeight] = React.useState<number | null>(0);
  React.useEffect(() => {
    const onClickOutside = (event) => {
      !dropdownReference.current?.contains(event.target) && setOpen(false);
    };
    if (innerOpen) {
      window.addEventListener("click", onClickOutside);
    } else {
      window.removeEventListener("click", onClickOutside);
    }
    return () => window.removeEventListener("click", onClickOutside);
  }, [innerOpen]);
  React.useEffect(() => {
    if (innerOpen && menuReference.current) {
      const menuRect = menuReference.current.getBoundingClientRect();
      const menuOutsideViewPort = isOutsideViewport(menuRect);
      const menuIsTopHalf = innerDropUp
        ? menuRect.bottom < window.innerHeight / 2
        : menuRect.top < window.innerHeight / 2;
      // const menuIsLeftHalf = menuRect.left < window.innerWidth / 2;

      if (menuOutsideViewPort) {
        if (menuOutsideViewPort.bottom) {
          if (menuIsTopHalf) {
            setMaxHeight(
              menuRect.height + menuOutsideViewPort.bottom - VIEWPORT_MARGIN
            );
          } else {
            setMaxHeight(null);
            setDropUp(true);
            // defer(this.onOpen);
          }
        }

        if (menuOutsideViewPort.top) {
          if (menuIsTopHalf) {
            setMaxHeight(null);
            setDropUp(false);
            // defer(this.onOpen);
          } else {
            setMaxHeight(
              menuRect.height + menuOutsideViewPort.top - VIEWPORT_MARGIN
            );
          }
        }

        if (menuOutsideViewPort.right) {
          setPullRight(true);
        }

        if (menuOutsideViewPort.left) {
          setPullRight(false);
        }

        onToggle && onToggle(innerOpen);
      }
    } else {
      onToggle && onToggle(false);
    }
  }, [innerDropUp, innerOpen, innerPullRight, onToggle]);
  const onClickToggle = React.useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onToggle && onToggle(!innerOpen);
      setOpen(!innerOpen);
    },
    [onToggle, innerOpen]
  );
  React.useEffect(() => {
    setOpen(open);
  }, [open]);
  return (
    <Container ref={dropdownReference} className={className}>
      {React.Children.map(children, (child: React.ReactElement) => {
        if (isDropdownElement(child, Toggle.displayName)) {
          return React.cloneElement(child, {
            key: "dropdownToggle",
            onClick: onClickToggle,
          });
        }
        if (isDropdownElement(child, Menu.displayName || "")) {
          return React.cloneElement(child, {
            key: "dropdownMenu",
            reference: menuReference,
            maxHeight,
            dropUp: innerDropUp,
            open: innerOpen,
            pullRight: innerPullRight,
            onClose: () => setOpen(false),
          });
        }
        return child;
      })}
    </Container>
  );
};

export default KomonDropdown;
